import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import CenterSpinner from '../../components/CenterSpinner';
import {
  ColumnHeaderType,
  LoggedUserType,
  UserType,
  ViewTaskFilterType,
} from '../../utils/Types';
import KTable from '../../components/KTable';
import { getViewTaskFilterData, VIEW_TASK_HEADER } from './TaskAssignHelper';
import ViewTaskFilter from '../../filters/ViewTaskFilter';
import {
  API_STATUS,
  CLICK_ACTIONS,
  DEFAULT_PAGE_SIZE,
  LIST_VIEW_ITEM,
  TASK_ACTION,
} from '../../utils/Constant';
import {
  fetchTaskList,
  taskApiStatus,
  taskSelector,
} from '../../features/taskSlice';
import CreateTaskPage from './CreateTaskPage';
import KPagination from '../../components/KPagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { dashboardSelector } from '../../features/dashboardSlice';
import { useAppDispatch, useAppSelector } from '../../store/redux-hooks';
import { loginSelector } from '../../features/loginSlice';
import classNames from 'classnames';
import ToggleSwitch from '../../components/ToggleSwitch';
import { onTableAction, onTaskAction } from '../assign-grid/AssignGridHelper';
import { toast } from 'react-toastify';

const ViewTaskPage: React.FunctionComponent = () => {
  const [viewTask, setViewTask] = useState(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedFilter, setSelectedFilter] = useState<ViewTaskFilterType>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedTask, setSelectedTask] = useState<any>();
  const [previousScreenData, setPreviousScreenData] = useState<any>();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const taskDataList = useAppSelector(taskSelector).taskListData.result ?? [];
  const pagination = useAppSelector(taskSelector).taskListData.pagination;
  const apiStatus = useAppSelector(taskApiStatus);
  const listOrView = useAppSelector(dashboardSelector).listOrView;
  const user: LoggedUserType = useAppSelector(loginSelector).user;
  const userDetails: UserType = useAppSelector(loginSelector).userDetails;
  const [showFilter, setShowFilter] = useState<boolean>(true);

  const navigate = useNavigate();

  const [previousLocation, setPreviousLocation] = useState<string | null>(null);
  const [pendingTaskIdValidation, setPendingTaskIdValidation] = useState<
    string | null
  >(null);
  const [apiFinished, setApiFinished] = useState(false);

  //Track the previous location and state
  useEffect(() => {
    const taskId: string = location?.state?.taskId;
    if (taskId) {
      setSelectedTask({ taskId: taskId });
      setViewTask(true);
      setPreviousScreenData(location?.state);
      setPreviousLocation('/view-task');
    }
  }, [location]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log('Popstate event triggered', event);
      setViewTask(false);

      if (!viewTask && previousLocation) {
        console.log('Navigating back to View Task page');
        navigate(previousLocation, { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, viewTask, previousLocation]);

  const getTaskList = () => {
    const data = getViewTaskFilterData(selectedFilter);
    dispatch(
      fetchTaskList({
        ...data,
        pageNumber: currentPage,
        pagePerCount: pageSize,
      })
    );
  };
  useEffect(() => {
    if (viewTask) {
      const url = new URL(window.location.href);
      url.searchParams.set('viewTask', 'true');
      window.history.pushState({ viewTask: true }, '', url.toString());
    }
  }, [viewTask]);

  useEffect(() => {
    if (listOrView === LIST_VIEW_ITEM.LIST_ITEMS) {
      setViewTask(false);
      getTaskList();
    }
  }, [listOrView]);

  useEffect(() => {
    if (apiStatus && apiStatus === API_STATUS.LOADING) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
    if (apiStatus === API_STATUS.SUCCEEDED) {
      setApiFinished(true);
    }
  }, [apiStatus]);

  useEffect(() => {
    if (userDetails?.roleid) getTaskList();
  }, [pageSize, currentPage, selectedFilter, userDetails, user]);

  const handleToggleSwitch = () => {
    setShowFilter((prev) => !prev);
  };

  useEffect(() => {
    if (pendingTaskIdValidation && apiFinished) {
      const matchFound = taskDataList?.some((task) =>
        task?.taskId
          ?.toLowerCase()
          .includes(pendingTaskIdValidation?.toLowerCase())
      );

      if (!matchFound) {
        toast.error('No task to Display');
      }
      setPendingTaskIdValidation(null);
      setApiFinished(false);
    }
  }, [pendingTaskIdValidation, apiFinished, taskDataList]);

  return (
    <>
      {!viewTask && (
        <div className="ml-4">
          <Label className="text-heading">View Tasks</Label>
          <ToggleSwitch isOpen={showFilter} onClick={handleToggleSwitch} />
          <div className="task-item-parent-view">
            {showFilter && (
              <ViewTaskFilter
                onApplyFilters={(filter: ViewTaskFilterType) => {
                  setSelectedFilter(filter);
                  setCurrentPage(1);
                  if (filter.taskId?.trim()) {
                    setPendingTaskIdValidation(filter.taskId.trim());
                    setApiFinished(false); // we'll wait for new data
                  } else {
                    setPendingTaskIdValidation(null);
                  }
                }}
              />
            )}
            <div className="mt-4">
              <KTable
                data={taskDataList}
                tableParentClass={classNames('table-parent-view-task', {
                  hideFilters: !showFilter,
                })}
                columnHeader={VIEW_TASK_HEADER}
                onAction={(param: any, headers: ColumnHeaderType) => {
                  setPreviousScreenData({
                    action: CLICK_ACTIONS.CLOSE_VIEW_TASK,
                  });
                  onTableAction(
                    param,
                    headers,
                    setSelectedTask,
                    setViewTask,
                    dispatch
                  );
                }}
                isLoading={showLoader}
              />
              {taskDataList && taskDataList.length > 0 && (
                <KPagination
                  totalPages={pagination?.totalPages}
                  currentPage={pagination?.currentPage}
                  pageSize={pagination?.pageSize}
                  onPageChange={setCurrentPage}
                  onSizeChange={setPageSize}
                  fromItem={pagination?.fromItem}
                  toItem={pagination?.toItem}
                  totalItems={pagination?.totalItems}
                />
              )}
            </div>
          </div>

          <CenterSpinner showLoader={showLoader} className="spinner-60" />
        </div>
      )}
      {viewTask && (
        <CreateTaskPage
          title="View Task"
          onTaskAction={() => {
            onTaskAction(
              previousScreenData,
              navigate,
              dispatch,
              setViewTask,
              getTaskList
            );
          }}
          taskStatusName={TASK_ACTION.VIEW}
          taskToView={selectedTask}
          scrollToBottom={location?.state?.scrollToBottom || false}
        />
      )}
    </>
  );
};

export default ViewTaskPage;
